import React, { useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

const AvatarPlayer = ({ room, isConnected, isStarting, sessionActive }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!room || !isConnected) return;

    const handleTrackSubscribed = (track, publication, participant) => {
      console.log("[AvatarPlayer] Track subscribed:", track.kind, track.sid);

      if (track.kind === "video") {
        const element = track.attach();
        if (videoRef.current) {
          videoRef.current.innerHTML = "";
          element.style.width = "100%";
          element.style.height = "100%";
          element.style.objectFit = "cover";
          element.style.borderRadius = "12px";
          videoRef.current.appendChild(element);
        }
      }

      if (track.kind === "audio") {
        // Detach any previous audio elements
        track.detach().forEach((el) => el.remove());

        // Create a fresh audio element and attach
        const audioEl = track.attach();
        audioEl.setAttribute("autoplay", "true");
        audioEl.setAttribute("playsinline", "true");
        audioEl.volume = 1.0;

        if (audioRef.current) {
          audioRef.current.innerHTML = "";
          audioRef.current.appendChild(audioEl);
        }

        // Force play to handle autoplay policy
        const playPromise = audioEl.play();
        if (playPromise) {
          playPromise.catch((err) => {
            console.warn("[AvatarPlayer] Audio autoplay blocked, will retry on user interaction:", err);
          });
        }

        console.log("[AvatarPlayer] Audio track attached and playing");
      }
    };

    const handleTrackUnsubscribed = (track) => {
      console.log("[AvatarPlayer] Track unsubscribed:", track.kind);
      track.detach().forEach((el) => el.remove());
    };

    room.on("trackSubscribed", handleTrackSubscribed);
    room.on("trackUnsubscribed", handleTrackUnsubscribed);

    // Attach any already-subscribed tracks
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.isSubscribed && publication.track) {
          handleTrackSubscribed(publication.track, publication, participant);
        }
      });
    });

    return () => {
      room.off("trackSubscribed", handleTrackSubscribed);
      room.off("trackUnsubscribed", handleTrackUnsubscribed);
    };
  }, [room, isConnected]);

  // Handle room audio output — use startAudio to unlock browser autoplay
  useEffect(() => {
    if (!room || !isConnected) return;

    // LiveKit's startAudio() resolves the browser autoplay policy
    room.startAudio().then(() => {
      console.log("[AvatarPlayer] Room audio started (autoplay unlocked)");
    }).catch((err) => {
      console.warn("[AvatarPlayer] Failed to start room audio:", err);
    });
  }, [room, isConnected]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "360px",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#0a0e1a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!isConnected && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {isStarting ? (
            <>
              <CircularProgress sx={{ color: "#7C4DFF" }} />
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Connecting to Digital Twin...
              </Typography>
            </>
          ) : !sessionActive ? (
            <Typography variant="body2" sx={{ color: "#555" }}>
              Click "Start Session" to activate the Digital Twin
            </Typography>
          ) : (
            <>
              <CircularProgress sx={{ color: "#7C4DFF" }} />
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Loading avatar stream...
              </Typography>
            </>
          )}
        </Box>
      )}
      <Box
        ref={videoRef}
        sx={{
          width: "100%",
          height: "100%",
          display: isConnected ? "block" : "none",
        }}
      />
      {/* Audio container — visible but off-screen to avoid autoplay blocking */}
      <Box
        ref={audioRef}
        sx={{
          position: "absolute",
          width: 0,
          height: 0,
          overflow: "hidden",
        }}
      />
    </Box>
  );
};

export default AvatarPlayer;
